import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { AutoDismissFlag, FlagGroup } from '@atlaskit/flag';
import Form, { CheckboxField, Field } from '@atlaskit/form';
import CrossCircleIcon from '@atlaskit/icon/core/cross-circle';
import ErrorIcon from '@atlaskit/icon/core/error';
import ChevronLeftIcon from '@atlaskit/icon/utility/chevron-left';
import Modal, { ModalTransition } from '@atlaskit/modal-dialog';
import { postReview } from '@atlassian/mpac-api-client';
import { breakpointValues, Button, H4, RatingsSelector, useResponsiveBreakpoint } from '@atlassian/mpac-primitives';
import { HostingTypeToQueryParam } from '@atlassian/mpac-types';
import { log } from '@atlassian/mpac-utils';
import React, { useCallback, useEffect, useState } from 'react';
import { useAsync } from 'react-async';
import { Body, ErrorText, ErrorWrapper, Footer, Header, HeaderMobile, NavBackButton, Overlay, RatingsLabel, RatingsWrapper, RequiredMessage, ReviewLabel, ReviewTextArea, UserConsentCheckbox, UserConsentCheckboxWrapper } from './ReviewFormModal.styles';
import { validateOnSubmit, validateRatings, validateUserComplianceField } from './ReviewFormModal.utils';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const ErrorMessage = _ref => {
  let {
    message
  } = _ref;
  return /*#__PURE__*/_jsxs(ErrorWrapper, {
    children: [/*#__PURE__*/_jsx(ErrorIcon, {
      label: "error",
      color: "var(--ds-icon-danger, #C9372C)"
    }), /*#__PURE__*/_jsx(ErrorText, {
      children: message
    })]
  });
};
// eslint-disable-next-line max-lines-per-function
const ReviewForm = _ref2 => {
  let {
    postReviewUrl,
    hosting,
    selfUserReview,
    isReviewApplicableForComplianceLogic,
    onReviewPost,
    onClose
  } = _ref2;
  const [showReviewBanner, setShowReviewBanner] = useState(null);
  const isEdit = selfUserReview === null || selfUserReview === void 0 ? void 0 : selfUserReview.stars;
  const {
    isPending: isPostReviewInProgress,
    isRejected: isPostReviewRejected,
    run: runPostReview,
    data: postedReview,
    error: postReviewError
  } = useAsync({
    deferFn: postReview
  });
  const postedReviewId = postedReview === null || postedReview === void 0 ? void 0 : postedReview.id;
  useEffect(() => {
    if (isPostReviewRejected) {
      setShowReviewBanner('failure');
      log.error('ReviewsTab', 'failed to post a review', {
        error: postReviewError
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPostReviewRejected]);
  useEffect(() => {
    if (!postedReviewId) return;
    onReviewPost(postedReview, !!isEdit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postedReviewId]);
  const setFocusOnHeading = useCallback(ref => {
    setTimeout(() => ref === null || ref === void 0 ? void 0 : ref.focus());
  }, []);
  const onReviewSubmit = data => {
    const errors = validateOnSubmit(data);
    const reviewStatus = (selfUserReview === null || selfUserReview === void 0 ? void 0 : selfUserReview.status) === 'approved' ? 'approved' : 'pending';
    const {
      userHasComplianceConsent,
      review,
      ratings
    } = data;
    if (Object.keys(errors).length === 0) {
      const postReviewData = _objectSpread({
        userHasComplianceConsent,
        review,
        stars: ratings,
        hosting: HostingTypeToQueryParam[hosting]
      }, isReviewApplicableForComplianceLogic && {
        status: reviewStatus
      });
      runPostReview(postReviewUrl, postReviewData);
    }
    return errors;
  };
  const resetReviewBanner = () => {
    setShowReviewBanner(null);
  };
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(FlagGroup, {
      children: showReviewBanner === 'failure' && /*#__PURE__*/_jsx(AutoDismissFlag, {
        id: "review-not-submitted",
        icon: /*#__PURE__*/_jsx(CrossCircleIcon, {
          label: "Failure",
          color: "var(--ds-icon-danger, #C9372C)"
        }),
        title: "Review not submitted",
        description: "We\u2019re are unable to load your review. Refresh the page or try again in some time.",
        onDismissed: resetReviewBanner,
        actions: [{
          content: 'Got it',
          onClick: resetReviewBanner
        }]
      }, 1)
    }), /*#__PURE__*/_jsx(Form, {
      onSubmit: onReviewSubmit,
      isDisabled: isPostReviewInProgress,
      children: _ref3 => {
        let {
          formProps
        } = _ref3;
        return /*#__PURE__*/_jsxs("form", _objectSpread(_objectSpread({}, formProps), {}, {
          children: [/*#__PURE__*/_jsx(Header, {
            tabIndex: -1,
            ref: setFocusOnHeading,
            children: /*#__PURE__*/_jsxs(H4, {
              as: "h1",
              children: [isEdit ? 'Edit your' : 'Write a', " review"]
            })
          }), /*#__PURE__*/_jsxs(Body, {
            children: [/*#__PURE__*/_jsxs(RequiredMessage, {
              children: ["Required fields are marked with an asterisk", /*#__PURE__*/_jsx("span", {
                children: "*"
              })]
            }), /*#__PURE__*/_jsxs(RatingsWrapper, {
              role: "group",
              "aria-labelledby": "select-your-rating-label",
              children: [/*#__PURE__*/_jsxs(RatingsLabel, {
                id: "select-your-rating-label",
                children: ["Select your rating", /*#__PURE__*/_jsx("span", {
                  children: "*"
                })]
              }), /*#__PURE__*/_jsx(Field, {
                name: "ratings",
                validate: validateRatings,
                defaultValue: selfUserReview === null || selfUserReview === void 0 ? void 0 : selfUserReview.stars,
                children: _ref4 => {
                  let {
                    fieldProps,
                    error
                  } = _ref4;
                  return /*#__PURE__*/_jsxs(_Fragment, {
                    children: [/*#__PURE__*/_jsx(RatingsSelector, {
                      currentRating: fieldProps.value,
                      onRatingsChange: fieldProps.onChange
                    }), /*#__PURE__*/_jsx("div", {
                      "aria-live": "polite",
                      role: "status",
                      "aria-relevant": "all",
                      children: error && /*#__PURE__*/_jsx(ErrorMessage, {
                        message: error
                      })
                    })]
                  });
                }
              })]
            }), /*#__PURE__*/_jsx(Field, {
              name: "review",
              defaultValue: selfUserReview === null || selfUserReview === void 0 ? void 0 : selfUserReview.review,
              children: _ref5 => {
                let {
                  fieldProps
                } = _ref5;
                return /*#__PURE__*/_jsxs(_Fragment, {
                  children: [/*#__PURE__*/_jsx(ReviewLabel, {
                    htmlFor: fieldProps === null || fieldProps === void 0 ? void 0 : fieldProps.id,
                    children: "Share what you liked/disliked about this app"
                  }), /*#__PURE__*/_jsx(ReviewTextArea, _objectSpread(_objectSpread({}, fieldProps), {}, {
                    appearance: "standard",
                    resize: "auto",
                    maxHeight: "10vh",
                    name: "review",
                    placeholder: "Write your honest review in detail, like the ones you prefer to read",
                    minimumRows: 4
                  }))]
                });
              }
            }), /*#__PURE__*/_jsx(UserConsentCheckboxWrapper, {
              children: /*#__PURE__*/_jsx(CheckboxField, {
                name: "userHasComplianceConsent",
                validate: validateUserComplianceField,
                defaultIsChecked: !!selfUserReview,
                children: _ref6 => {
                  let {
                    fieldProps,
                    error
                  } = _ref6;
                  return /*#__PURE__*/_jsxs(_Fragment, {
                    children: [/*#__PURE__*/_jsx(UserConsentCheckbox, _objectSpread(_objectSpread({}, fieldProps), {}, {
                      name: "userHasComplianceConsent",
                      label: "You are currently using this app or have used it in the last six months"
                    })), error && /*#__PURE__*/_jsx(ErrorMessage, {
                      message: error
                    })]
                  });
                }
              })
            })]
          }), /*#__PURE__*/_jsxs(Footer, {
            children: [/*#__PURE__*/_jsx(Button, {
              onClick: onClose,
              appearance: "subtle",
              children: "Cancel"
            }), /*#__PURE__*/_jsx(Button, {
              type: "submit",
              isLoading: isPostReviewInProgress,
              appearance: "primary",
              children: isEdit ? 'Update' : 'Submit'
            })]
          })]
        }));
      }
    })]
  });
};
export const ReviewFormModal = props => {
  const {
    value: screenSize
  } = useResponsiveBreakpoint();
  return /*#__PURE__*/_jsx(ModalTransition, {
    children: /*#__PURE__*/_jsx(Modal, {
      label: "Review Form",
      onClose: props.onClose,
      children: /*#__PURE__*/_jsx(Overlay, {
        children: screenSize <= breakpointValues.xs ? /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(HeaderMobile, {
            children: /*#__PURE__*/_jsx(NavBackButton, {
              onClick: props.onClose,
              appearance: "subtle",
              iconBefore: /*#__PURE__*/_jsx(ChevronLeftIcon, {
                label: "Back",
                LEGACY_size: "large",
                color: "var(--ds-icon, #44546F)"
              }),
              children: "Back"
            })
          }), /*#__PURE__*/_jsx(ReviewForm, _objectSpread({}, props))]
        }) : /*#__PURE__*/_jsx(ReviewForm, _objectSpread({}, props))
      })
    })
  });
};